import { useEffect } from "preact/hooks";
import { battle } from "@/battle/BattleController";
import { returnToTitle } from "@/state/session";
import { calculatorOpen, dialog, openBackpack, panel, screen, settings, toast } from "@/state/store";
import { training } from "@/training/TrainingController";
import { BackpackPanel } from "./BackpackPanel";
import { BattlePanel } from "./BattlePanel";
import { Calculator } from "./Calculator";
import { CookieNotice } from "./CookieNotice";
import { DialogBox } from "./DialogBox";
import { HelpPanel } from "./HelpPanel";
import { Hud } from "./Hud";
import { ReportView } from "./ReportView";
import { ShopPanel } from "./ShopPanel";
import { StartPanel } from "./StartPanel";
import { Toolbar } from "./Toolbar";
import { TouchControls } from "./TouchControls";
import { TrainingPanel } from "./TrainingPanel";

export function App() {
  const current = screen.value;
  const overlayOpen = !!battle.value || !!dialog.value || !!training.value || !!panel.value;
  const report =
    panel.value === "report-history" ? "history" : panel.value === "report-curriculum" ? "curriculum" : null;
  const showCalculator =
    calculatorOpen.value &&
    settings.value.calculator &&
    (!!battle.value || training.value?.phase === "question");

  // I / Tab toggles the backpack in the world; Esc closes any panel; F2 toggles the calculator
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "F2" && settings.value.calculator && (battle.value || training.value)) {
        e.preventDefault();
        calculatorOpen.value = !calculatorOpen.value;
        return;
      }
      // F1 (or ?) opens the help anywhere except during a fight or a training question
      if (e.key === "F1" || (e.key === "?" && !(e.target instanceof HTMLInputElement))) {
        if (battle.value || training.value) return;
        e.preventDefault();
        panel.value = panel.value === "help" ? null : "help";
        return;
      }
      if (e.key === "Escape" && panel.value) {
        panel.value = null;
        return;
      }
      if (screen.value !== "world") return;
      if (e.target instanceof HTMLInputElement) return;
      const free = !battle.value && !training.value && !dialog.value;
      const key = e.key.toLowerCase();
      if ((key === "i" || e.key === "Tab") && free) {
        e.preventDefault();
        if (panel.value === "backpack") panel.value = null;
        else openBackpack("equipment");
        return;
      }
      if (key === "z" && free) {
        e.preventDefault();
        openBackpack("quests");
        return;
      }
      if (key === "p" && free) {
        e.preventDefault();
        openBackpack("progress");
        return;
      }
      if (e.key.toLowerCase() === "m" && panel.value === "backpack") {
        e.preventDefault();
        returnToTitle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      {current === "title" && <StartPanel />}
      {current === "title" && !panel.value && <CookieNotice />}
      {current === "world" && <Hud />}
      {current === "world" && !battle.value && !training.value && <Toolbar />}
      {current === "world" && !overlayOpen && <TouchControls />}
      {dialog.value && <DialogBox />}
      {battle.value && <BattlePanel />}
      {training.value && <TrainingPanel />}
      {showCalculator && <Calculator />}
      {panel.value === "backpack" && !battle.value && <BackpackPanel />}
      {panel.value === "shop" && !battle.value && <ShopPanel />}
      {panel.value === "help" && <HelpPanel />}
      {report && <ReportView mode={report} />}
      {toast.value && <div class="toast">{toast.value.text}</div>}
    </>
  );
}
